import { useEffect, useEffectEvent } from 'react';

/**
 * A step's data-sync rows with the wizard's ledger: `seeded` holds while
 * the step has already taken the current read's rows, `loaded` while its
 * table still shows them and Personio is linked; an Excel import, "Liste
 * leeren" or emptying the table row by row ends it (`onReplaced`).
 */
export interface PersonioSeed<TRows> {
  rows: TRows | undefined;
  seeded: boolean;
  onSeeded: () => void;
  loaded: boolean;
  onReplaced: () => void;
  /** The current read is the step's own "Aus Personio laden": it merges past the open rules */
  ownLoad: boolean;
  /** The step's own "Aus Personio laden" finished: the other steps keep their tables */
  onLoaded: () => void;
}

/**
 * Fills a step from the data-sync rows when it opens: once the rows and
 * the step's own data are there, unless the step already took this read's
 * rows (the ledger lives with the drafts, so a table the admin emptied is not
 * refilled on re-entry). `seed` decides on the current table and returns
 * whether the step took the rows. An effect because the trigger is query data
 * arriving after the step opened, not a user event.
 */
export function useSeedOnOpen<TRows>({
  personioSeed,
  ready,
  seed
}: {
  personioSeed: PersonioSeed<TRows> | undefined;
  ready: boolean;
  seed: (rows: TRows) => boolean;
}) {
  const rows = personioSeed?.rows;
  const seeded = personioSeed?.seeded ?? true;
  const ownLoad = personioSeed?.ownLoad ?? false;
  const seedIfDue = useEffectEvent((due: TRows | undefined) => {
    if (!due || !ready || seeded) return;
    if (seed(due)) personioSeed?.onSeeded();
  });
  useEffect(
    function seedWhenDue() {
      seedIfDue(rows);
    },
    [rows, ready, seeded, ownLoad]
  );
}
